import React from "react";
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import YouTube from "react-youtube";
import SweetAlert from "components/react-bootstrap-sweetalert/SweetAlert.jsx";
import { FaYoutube } from "react-icons/fa";
import {
  Button,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardImg,
} from "reactstrap";
import { LargeMediaViewer } from "components";
import {
  parseMediaTypeFromUrl,
  getStreamGalleryMediaURL,
} from "_helpers";
import { notificationMessageActions } from "_actions";

import defaultImage from "assets/img/image_placeholder.jpg";

import setCanvasPreview from "./setCanvasPreview";

// --- Constants ---
const opts = {
  height: "600",
  width: "600",
  playerVars: { autoplay: 0 },
};

const ASPECT_RATIO = 1;
const MIN_DIMENSION = 150; // px

class GalleryUpload extends React.Component {
  // Stable refs (do NOT put in state)
  imgRef = React.createRef();
  previewCanvasRef = React.createRef();

  constructor(props) {
    super(props);

    this.state = {
      file: null,
      blob: null,
      croppedImageUrl: null,
      imagePreviewUrl: this.props.avatar
        ? defaultImage
        : this.props.imageUrl
          ? this.props.imageUrl
          : defaultImage,
      index: props.index !== undefined ? props.index : null,

      // Crop & source
      src: null, // raw image DataURL for cropper
      imgSrc: "", // same as src; preserved usage
      crop: {
        aspect: ASPECT_RATIO,
        width: 50,
        x: 0,
        y: 0,
      },

      // UI
      modalNotice: false,
      mediaViewerModal: false,
      mountedTimestamp: props.mountedTimestamp
        ? props.mountedTimestamp
        : new Date().getTime(),
      alert: null,

      // Validation
      error: "",
    };
  }

  // ---------- Utilities / Setters ----------
  setCrop = (crop) => this.setState({ crop });
  setImgSrc = (imgSrc) => this.setState({ imgSrc, src: imgSrc });
  setError = (error) => this.setState({ error });

  toggleModalNotice = () =>
    this.setState((s) => ({ modalNotice: !s.modalNotice }));

  toggleMediaViewerModal = () =>
    this.setState((s) => ({ mediaViewerModal: !s.mediaViewerModal }));

  closeModalNotice = () => {
    this.setState({
      modalNotice: false,
      src: null,
      imgSrc: "",
      error: "",
      // NOTE: do NOT reset refs here
    });
  };

  cancelImageUpload = () => {
    this.setState({
      src: null,
      imgSrc: "",
      error: "",
      file: null,
      blob: null,
    });
  };

  // ---------- File Select / Validation ----------
  onSelectFile = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const isImage = file.type.includes("image/");
    const isVideo = file.type.includes("video/");

    if (isImage) {
      if (file.size > 7_500_000) {
        this.props.dispatch(
          notificationMessageActions.addNotificationToQueue(
            "Your image must be less than 7.5 mb.",
            2
          )
        );
        return;
      }

      const reader = new FileReader();
      reader.addEventListener("load", () => {
        const imageUrl = reader.result?.toString() || "";
        const imageElement = new Image();
        imageElement.src = imageUrl;

        imageElement.addEventListener("load", (ev) => {
          const naturalWidth = ev.currentTarget.naturalWidth;
          const naturalHeight = ev.currentTarget.naturalHeight;
          if (
            naturalWidth < MIN_DIMENSION ||
            naturalHeight < MIN_DIMENSION
          ) {
            this.setError(
              "Image must be at least 150 x 150 pixels."
            );
            this.setImgSrc("");
            return;
          }
          // ok
          if (this.state.error) this.setError("");
          this.setImgSrc(imageUrl);
        });
      });

      reader.readAsDataURL(file);
      return;
    }

    if (isVideo) {
      if (file.size > 100_000_000) {
        this.props.dispatch(
          notificationMessageActions.addNotificationToQueue(
            "Videos must be less than 100 mb. If the video size limit is too small, you can upload the full-sized video to YouTube and add its url instead.",
            2
          )
        );
        return;
      }

      // Non-image/video fallback (shouldn’t happen with accept attr)
      const reader = new FileReader();
      reader.onloadend = () => {
        this.setState({
          file,
          imagePreviewUrl: reader.result,
        });
        this.props.callback({ state: this.state, newImage: true });
      };
      reader.readAsDataURL(file);
      return;
    }

    // Other file types: just load & preview
    const reader = new FileReader();
    reader.onloadend = () => {
      this.setState({
        file,
        imagePreviewUrl: reader.result,
      });
      this.props.callback({ state: this.state, newImage: true });
    };
    reader.readAsDataURL(file);
  };

  // ---------- YouTube helpers ----------
  addYoutubeVideoAlert = () => {
    this.setState({
      alert: (
        <SweetAlert
          input
          showCancel
          style={{ display: "block", marginTop: "-100px" }}
          title="Enter YouTube Video URL"
          onConfirm={(value) => this.confirmYoutubeVideo(value)}
          onCancel={this.hideAlert}
          confirmBtnBsStyle="primary"
          cancelBtnBsStyle="default"
          confirmBtnText="Confirm"
          cancelBtnText="Cancel"
          btnSize="sm"
        />
      ),
    });
  };

  hideAlert = () => this.setState({ alert: null });

  confirmYoutubeVideo = (url) => {
    this.setState({ alert: url });
    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url?.match(regExp);
    if (match && match[7]?.length === 11) {
      const parsedId = match[7];
      if (parsedId && this.state.index !== null) {
        this.props.youtubeCallback(parsedId, this.state.index);
        this.closeModalNotice();
      } else {
        this.props.dispatch(
          notificationMessageActions.addNotificationToQueue(
            "Please refresh the page.",
            2
          )
        );
      }
    } else {
      this.props.dispatch(
        notificationMessageActions.addNotificationToQueue(
          "Please try a different version of that YouTube URL. We were unable to verify it.",
          2
        )
      );
    }
    this.hideAlert();
  };

  handleMediaType = (type) => {
    this.hideAlert();
    if (type === "media") {
      this.refs.fileInput?.click();
    } else if (type === "youtube") {
      this.addYoutubeVideoAlert();
    }
  };

  _onReady = (event) => {
    event.target.pauseVideo();
  };

  // ---------- Crop lifecycle ----------
  onImageLoad = (e) => {
    const img = e.currentTarget;
    const width = img.naturalWidth;
    const height = img.naturalHeight;

    // Ensure the initial crop is at least MIN_DIMENSION wide in px
    const minPct = (MIN_DIMENSION / width) * 100;

    const base = makeAspectCrop(
      { unit: "%", width: Math.min(100, Math.max(minPct, 1)) },
      ASPECT_RATIO,
      width,
      height
    );

    const centered = centerCrop(base, width, height);
    this.setCrop(centered);
  };

  // Save the cropped image: canvas dataURL + Blob
  performCrop = async () => {
    const img = this.imgRef.current;
    const canvas = this.previewCanvasRef.current;
    const { crop } = this.state;

    if (!img || !canvas || !crop) return;

    const pixelCrop = convertToPixelCrop(
      crop,
      img.naturalWidth,
      img.naturalHeight
    );

    try {
      // setCanvasPreview may be sync or async; normalize with Promise.resolve
      await Promise.resolve(setCanvasPreview(img, canvas, pixelCrop));
      const dataUrl = canvas.toDataURL("image/png");

      // If you need a Blob (recommended for uploads):
      await new Promise((resolve) =>
        canvas.toBlob((blob) => {
          this.updateAvatar(dataUrl, blob || null);
          resolve();
        }, "image/png")
      );
    } catch (err) {
      console.error("Crop failed:", err);
      // Fallback: still try to capture a dataURL
      const dataUrl = canvas.toDataURL("image/png");
      this.updateAvatar(dataUrl, null);
    }
  };

  updateAvatar = (imgSrc, blob) => {
    this.setState({ croppedImageUrl: imgSrc, blob });
  };

  handleImageChange = (e) => {
    e?.preventDefault?.();
    const { blob } = this.state;
    if (!blob) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      this.setState({ imagePreviewUrl: reader.result });
      this.props.callback({ state: this.state, newImage: true });
      this.closeModalNotice();
    };
    reader.readAsDataURL(blob);
  };

  render() {
    const {
      crop,
      croppedImageUrl,
      src,
      imagePreviewUrl,
      mountedTimestamp,
      modalNotice,
      mediaViewerModal,
      imgSrc,
      alert,
    } = this.state;

    const prefersDark = localStorage.getItem("darkMode") === "true";

    const mediaExt = imagePreviewUrl.split(".").pop();
    const mediaType = parseMediaTypeFromUrl(imagePreviewUrl); // "image" | "video" | ...
    const isVideo = mediaType === "video";
    const isPhoto = mediaType === "image";

    // “YouTube id” case: your original condition (preserved)
    const isYoutube =
      !isPhoto &&
      !isVideo &&
      !this.state.imagePreviewUrl.includes("http") &&
      this.state.imagePreviewUrl.length > 0;

    const videoId =
      isVideo && imagePreviewUrl.substring(imagePreviewUrl.lastIndexOf("/") + 1);
    const streamVideoUrl = isVideo && getStreamGalleryMediaURL(videoId);

    return (
      <div className="fileinput text-center" style={{ display: "inherit" }}>
        {alert}
        <input
          type="file"
          className="fileinput"
          accept=".mov, .mp4, .jpg, .jpeg, .png, .quicktime,"
          onChange={this.onSelectFile}
          ref="fileInput"
        />

        <Card className="mb-0">
          <div>
            {isVideo && (
              <div
                className="embed-responsive embed-responsive-1by1 card-img-top cursor-pointer"
                onClick={this.toggleMediaViewerModal}
              >
                <video
                  onContextMenu={(e) => e.preventDefault()}
                  width="320"
                  height="240"
                  controls
                  autoPlay={false}
                  style={{ pointerEvents: "none" }}
                >
                  <source
                    src={streamVideoUrl}
                    type={`video/${mediaExt}`}
                    autostart="false"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
          </div>

          {isYoutube && (
            <div onClick={this.toggleMediaViewerModal}>
              <YouTube
                videoId={this.state.imagePreviewUrl}
                opts={opts}
                onReady={this._onReady}
                containerClassName="embed-responsive embed-responsive-1by1 card-img-top cursor-pointer"
                className="pointer-events-none"
              />
            </div>
          )}

          {isPhoto && (
            <CardImg
              alt="..."
              src={`${imagePreviewUrl}?t=${mountedTimestamp}`}
              onClick={this.toggleMediaViewerModal}
              className="cursor-pointer"
              top
            />
          )}
        </Card>

        <div className="mt-3">
          <Button
            className="btn-primary"
            size="sm"
            color="primary"
            onClick={this.toggleModalNotice}
          >
            {this.props.imageUrl ? "Update Media" : "Add Media"}
          </Button>
          <br />
        </div>

        <Row>
          <Modal
            isOpen={modalNotice}
            toggle={this.toggleModalNotice}
            className="modal-notice"
            size="lg"
            fade={false}
            onClosed={this.closeModalNotice}
          >
            <ModalHeader toggle={this.toggleModalNotice}>
              Select Media Type
            </ModalHeader>

            <ModalBody>
              {imgSrc ? (
                <div className="text-center">
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => this.setCrop(percentCrop)}
                    keepSelection
                    aspect={ASPECT_RATIO}
                    minWidth={MIN_DIMENSION}
                  >
                    <img
                      ref={this.imgRef}
                      src={imgSrc}
                      alt="Upload"
                      style={{ maxHeight: "70vh" }}
                      onLoad={this.onImageLoad}
                    />
                  </ReactCrop>

                  <Row className="text-center mx-auto pt-2">
                    <Button
                      outline
                      size="sm"
                      color="info"
                      className="mx-auto"
                      onClick={this.performCrop}
                    >
                      Crop Image
                    </Button>
                  </Row>

                  {crop && (
                    <canvas
                      ref={this.previewCanvasRef}
                      className="mt-4"
                      style={{
                        border: "1px solid black",
                        objectFit: "contain",
                        width: 150,
                        height: 150,
                      }}
                    />
                  )}

                  <div className="pt-2">
                    {this.imgRef.current ? (
                      <span>
                        <Button
                          outline
                          size="sm"
                          color={prefersDark ? "spc-white" : "default"}
                          onClick={this.cancelImageUpload}
                        >
                          Change
                        </Button>
                        {this.props.avatar ? <br /> : null}
                        <Button
                          outline
                          size="sm"
                          color="primary"
                          onClick={this.handleImageChange}
                        >
                          Save
                        </Button>
                      </span>
                    ) : (
                      <span />
                    )}

                    {this.state.file !== null && (
                      <Button
                        outline
                        size="sm"
                        color={prefersDark ? "spc-white" : "default"}
                        onClick={this.cancelImageUpload}
                      >
                        Change
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <Button
                    className="btn-primary"
                    size="sm"
                    color="primary"
                    onClick={() => this.handleMediaType("media")}
                  >
                    {this.state.imagePreviewUrl === defaultImage
                      ? "Add Media"
                      : "Update Media"}
                  </Button>
                  <br />
                  <Button
                    className="btn-youtube"
                    style={{ marginTop: "10px" }}
                    size="sm"
                    onClick={() => this.handleMediaType("youtube")}
                  >
                    <FaYoutube color="#ffffff" size={25} />{" "}
                    &nbsp;&nbsp;&nbsp;&nbsp;{"Add YouTube Video"}
                  </Button>
                </div>
              )}
            </ModalBody>
          </Modal>
        </Row>

        <Row>
          <Modal
            isOpen={mediaViewerModal}
            toggle={this.toggleMediaViewerModal}
            modalClassName="text-center"
            fade={false}
            size="lg"
          >
            <ModalHeader toggle={this.toggleMediaViewerModal}>
              Media
            </ModalHeader>
            <ModalBody>
              {this.state.imagePreviewUrl && (
                <LargeMediaViewer
                  img
                  imageUrl={this.state.imagePreviewUrl}
                />
              )}
            </ModalBody>
          </Modal>
        </Row>
      </div>
    );
  }
}

export default GalleryUpload;